import {Trans} from '@lingui/macro';
import React, {FC, useEffect} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isIntegrationSettingFieldLoading} from '../../../../../../helpers/integrationHelpers';
import {useAppDispatch, useAppSelector} from '../../../../../../helpers/reduxHelpers';
import {
  SmoochIntegrationModel,
  SmoochIntegrationSettingsModel,
} from '../../../../../../models/api/integrationModel';
import {decryptIntegrationSettingsAsyncAction} from '../../../../../../store/data/integrations/integrationsActions';
import {getIsUserGlobalAdmin} from '../../../../../../store/data/teammates/teammatesSelectors';
import {maybeRenderConfigureDialogTextBox} from '../shared/settingsConfigureHelpers';

/*
 * Props.
 */

interface SettingsAppsConfigureSmoochProps {
  integration?: SmoochIntegrationModel;
  configurationSettings?: SmoochIntegrationSettingsModel;
  onIsSaveEnabled: HandlerOf<boolean>;
  setConfigurationSettings: HandlerOf<SmoochIntegrationSettingsModel | undefined>;
}

/*
 * Style.
 */

const StyledDescriptionWrapperDiv = styled.div`
  margin-bottom: 16px;
`;

/*
 * Component.
 */

export const SettingsAppsConfigureSmooch: FC<SettingsAppsConfigureSmoochProps> = (props) => {
  const {configurationSettings, integration, onIsSaveEnabled, setConfigurationSettings} = props;
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const dispatch = useAppDispatch();

  const isDecrypting =
    isIntegrationSettingFieldLoading(configurationSettings?.appSecret || '') ||
    isIntegrationSettingFieldLoading(configurationSettings?.keyId || '');

  // When we first load the settings, we may need to decrypt the settings.
  useInitialEffect(() => {
    if (!integration) {
      return;
    }
    dispatch(decryptIntegrationSettingsAsyncAction(integration));
  });

  // Controls if save is enabled for this dialog.
  //  This requires the api-key to be filled out, but there is no validation.
  useEffect(() => {
    if (!configurationSettings || isDecrypting) {
      onIsSaveEnabled(false);
      return;
    }
    const keyId = configurationSettings.keyId || '';
    const secret = configurationSettings.appSecret || '';
    onIsSaveEnabled(keyId.trim() !== '' && secret.trim() !== '');
  }, [configurationSettings, isDecrypting, onIsSaveEnabled]);

  return (
    <>
      <StyledDescriptionWrapperDiv>
        <Trans id="settings-apps-configure-app-dialog-smooch-description">
          Display Sunshine Conversations data information alongside conversations.
        </Trans>
      </StyledDescriptionWrapperDiv>
      {maybeRenderConfigureDialogTextBox(
        configurationSettings?.keyId || '',
        <Trans id="settings-apps-configure-app-dialog-smooch-key-id">Key ID</Trans>,
        (value) => {
          setConfigurationSettings({
            ...configurationSettings,
            keyId: value,
          });
        },
        {
          isDisabled: !isGlobalAdmin,
          isLoading: isDecrypting && isGlobalAdmin,
          isRequired: true,
        },
      )}
      {maybeRenderConfigureDialogTextBox(
        configurationSettings?.appSecret || '',
        <Trans id="settings-apps-configure-app-dialog-smooch-app-secret">Secret</Trans>,
        (value) => {
          setConfigurationSettings({
            ...configurationSettings,
            appSecret: value,
          });
        },
        {
          isDisabled: !isGlobalAdmin,
          isLoading: isDecrypting && isGlobalAdmin,
          isRequired: true,
        },
      )}
    </>
  );
};
